import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import styles from "./AppBanner.module.css";
import FleekIcon from "../../asset/icons/Brand_logo_Sq.png";
import { track, EVENTS } from "../../utils/analyticsService";
import { appLink, trackStoreLinkClick, STORES, PLACEMENTS } from "../../utils/storeLinks";
import { isBlocked } from "../../utils/market";
import {
  bannerPageType,
  canShowAndroidBanner,
  isDismissed,
  rememberDismissal,
  setAppBarShowing,
} from "../../utils/appBanner";

const PLATFORM = "android";

/**
 * "Track and cancel every subscription — get the Fleek app": a slim bar at
 * the top of the page for Android browsers (iOS gets Safari's own Smart App
 * Banner instead). In the page flow rather than fixed, so it scrolls away and
 * never covers the sticky buy bars at the bottom. Not on cart, checkout or
 * payment pages (utils/appBanner.js bannerPageType); × hides it for 30 days.
 */
export const AppBanner = () => {
  const { pathname } = useLocation();
  const page = bannerPageType(pathname);
  const [dismissed, setDismissed] = useState(() => isDismissed());
  const [eligible] = useState(() => canShowAndroidBanner() && !isBlocked());
  const visible = eligible && !dismissed && !!page;

  useEffect(() => {
    setAppBarShowing(visible);
  }, [visible]);

  useEffect(() => () => setAppBarShowing(false), []);

  // Once per page shown.
  useEffect(() => {
    if (visible) track(EVENTS.APP_BANNER_SHOWN, { platform: PLATFORM, page });
  }, [visible, page, pathname]);

  if (!visible) return null;

  const href = appLink({
    utm_source: "web",
    utm_medium: PLACEMENTS.SMART_BANNER,
    utm_campaign: page,
  });

  const onGet = () => {
    track(EVENTS.APP_BANNER_CLICKED, { platform: PLATFORM, page });
    trackStoreLinkClick(STORES.PLAY, PLACEMENTS.SMART_BANNER);
  };

  const onDismiss = () => {
    rememberDismissal();
    setDismissed(true);
    track(EVENTS.APP_BANNER_DISMISSED, { platform: PLATFORM, page });
  };

  return (
    <div className={styles.bar} role="complementary" aria-label="Get the Fleek app">
      <button type="button" className={styles.close} onClick={onDismiss} aria-label="Dismiss">
        ×
      </button>
      <img className={styles.icon} src={FleekIcon} alt="" width="32" height="32" />
      <p className={styles.text}>Track and cancel every subscription — get the Fleek app</p>
      <a className={styles.cta} href={href} onClick={onGet} target="_blank" rel="noreferrer">
        Get app
      </a>
    </div>
  );
};

export default AppBanner;
